import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import moment from 'moment';

import { PENDING_STOCK_SOURCE_TYPE } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';

import { ListItem } from '../../components/list/ListItem';
import { Button } from '../../components';
import Text from '../../components/text/Text';
import { StatusBadge } from '../../design/components';
import { getUnitsLabel } from '../../utils/unitsLabel';
import { useThemedStyles } from '../../hooks/useThemedStyles';

import { formatSourceLabel, getDriverName } from './PendingStockHelpers';
import PendingStockResolveDialog from './PendingStockResolveDialog';
import PendingStockExcessScanDialog from './PendingStockExcessScanDialog';
import usePendingStockResolve from './usePendingStockResolve';
import usePendingStockExcessScan from './usePendingStockExcessScan';

const PendingStockListItem = ({ item, customProps }) => {
	const styles = useThemedStyles(createStyles);
	const { tenant } = useUserContext();
	const timezone = item?.expiryPolicyContext?.timezone || tenant?.timezone;
	const {
		canModifyStock,
		canViewStockDeliveryDetail,
		onOpenStockDelivery,
		refetchList,
	} = customProps || {};

	const quantityInUnits = Math.max(0, Math.round(Number(item?.quantity) || 0));
	const createdAt = item?.created ? moment(item.created).format('DD MMM YYYY') : 'Unknown';
	const expiryDate = item?.expiryDate ? moment(item.expiryDate).format('DD MMM YYYY') : 'n/a';
	const runDate = item?.serviceRun?.planned ? moment(item.serviceRun.planned).format('DD MMM YYYY') : 'n/a';
	const sourceLabel = useMemo(() => formatSourceLabel(item), [item]);
	const productName = item?.product?.name || 'Unknown product';
	const warehouseName = item?.warehouse?.name || 'Unknown warehouse';
	const driverName = getDriverName(item?.serviceRun?.driver);
	const isDeliveryPendingStock = item?.sourceType === PENDING_STOCK_SOURCE_TYPE.DELIVERY;
	const canAddExcessStock = Boolean(canModifyStock && item?.warehouse?.id && item?.serviceRun?.id && !isDeliveryPendingStock);
	const warehouseProductForResolve = item?.warehouseProduct || { product: item?.product };

	const {
		moveLoading,
		discardLoading,
		isResolveDialogOpen,
		setIsResolveDialogOpen,
		resolveBatchRows,
		updateResolveBatchRow,
		addResolveBatchRow,
		removeResolveBatchRow,
		normalizedResolveQuantity,
		isResolveShortDated,
		hasExpiredResolveStock,
		hasUnknownResolveExpiryDate,
		resolveExpiryDateRecommended,
		isRequiredResolveExpiryDateMissing,
		canSubmitResolve,
		moveToWarehouse,
		discardFromPendingStock,
	} = usePendingStockResolve({
		item,
		quantityInUnits,
		timezone,
		refetchList,
	});

	const {
		addExcessLoading,
		productLookupLoading,
		isScanDialogOpen,
		scannedBarcode,
		scannedProduct,
		excessBatchRows,
		updateExcessBatchRow,
		addExcessBatchRow,
		removeExcessBatchRow,
		normalizedExcessQuantity,
		isExcessShortDated,
		canSubmitExcessStock,
		warehouseProductForExcess,
		cameraPermission,
		requestCameraPermission,
		handleBarcodeScanned,
		isScanning,
		openScanDialog,
		closeScanDialog,
		scanAgain,
		submitExcessStock,
	} = usePendingStockExcessScan({
		item,
		timezone,
		canAddExcessStock,
		refetchList,
	});

	const loading = moveLoading || discardLoading || addExcessLoading;

	const openResolvePendingStock = useCallback(() => {
		if (!canModifyStock || !item?.id || !quantityInUnits || isDeliveryPendingStock) {
			return;
		}

		setIsResolveDialogOpen(true);
	}, [canModifyStock, isDeliveryPendingStock, item?.id, quantityInUnits, setIsResolveDialogOpen]);

	return (
		<>
			<ListItem style={styles.item}>
				<View style={styles.header}>
					<Text style={styles.title}>{productName}</Text>
					<StatusBadge label={`${quantityInUnits} ${getUnitsLabel(quantityInUnits)}`} tone="primary" size="small" />
				</View>
				<Text style={styles.meta}>Source: {sourceLabel}</Text>
				<Text style={styles.meta}>Driver: {driverName}</Text>
				<Text style={styles.meta}>Run date: {runDate}</Text>
				<Text style={styles.meta}>Warehouse: {warehouseName}</Text>
				<Text style={styles.meta}>Created: {createdAt}</Text>
				<Text style={styles.meta}>Expiry: {expiryDate}</Text>
				<View style={styles.actions}>
					{isDeliveryPendingStock ? (
						<Button
							style={styles.actionButton}
							label={item?.stockDelivery?.id ? 'Open delivery' : 'Delivery item'}
							onPress={() => {
								if (canViewStockDeliveryDetail && item?.stockDelivery?.id) {
									onOpenStockDelivery?.(item.stockDelivery.id);
								}
							}}
							secondary={true}
							disabled={!canViewStockDeliveryDetail || !item?.stockDelivery?.id}
						/>
					) : (
						<>
							<Button
								style={styles.actionButton}
								label="Resolve stock"
								onPress={openResolvePendingStock}
								secondary={true}
								loading={moveLoading || discardLoading}
								disabled={!canModifyStock || !item?.id || !quantityInUnits}
							/>
							<Button
								style={styles.actionButton}
								label="Scan excess"
								onPress={openScanDialog}
								loading={addExcessLoading || productLookupLoading}
								disabled={!canAddExcessStock || loading}
							/>
						</>
					)}
				</View>
			</ListItem>

			<PendingStockResolveDialog
				isOpen={isResolveDialogOpen}
				onClose={() => setIsResolveDialogOpen(false)}
				productName={productName}
				quantityInUnits={quantityInUnits}
				warehouseProduct={warehouseProductForResolve}
				rows={resolveBatchRows}
				updateRow={updateResolveBatchRow}
				addRow={addResolveBatchRow}
				removeRow={removeResolveBatchRow}
				normalizedQuantity={normalizedResolveQuantity}
				isShortDated={isResolveShortDated}
				hasExpiredStock={hasExpiredResolveStock}
				isRequiredExpiryDateMissing={isRequiredResolveExpiryDateMissing}
				isExpiryDateRecommended={resolveExpiryDateRecommended}
				hasUnknownExpiryDate={hasUnknownResolveExpiryDate}
				canSubmit={canSubmitResolve}
				onDiscard={discardFromPendingStock}
				onMoveToWarehouse={moveToWarehouse}
				moveLoading={moveLoading}
				discardLoading={discardLoading}
				loading={loading}
			/>

			<PendingStockExcessScanDialog
				isOpen={isScanDialogOpen}
				onClose={closeScanDialog}
				scannedProduct={scannedProduct}
				scannedBarcode={scannedBarcode}
				warehouseProduct={warehouseProductForExcess}
				rows={excessBatchRows}
				updateRow={updateExcessBatchRow}
				addRow={addExcessBatchRow}
				removeRow={removeExcessBatchRow}
				normalizedQuantity={normalizedExcessQuantity}
				isShortDated={isExcessShortDated}
				canSubmit={canSubmitExcessStock}
				onScanAgain={scanAgain}
				onSubmit={submitExcessStock}
				addLoading={addExcessLoading}
				cameraPermission={cameraPermission}
				requestCameraPermission={requestCameraPermission}
				onBarcodeScanned={isScanning || productLookupLoading ? undefined : handleBarcodeScanned}
				productLookupLoading={productLookupLoading}
			/>
		</>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	item: {
		marginHorizontal: space.gutter,
		marginTop: 0,
		marginBottom: space.sm,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		gap: space.xxs,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
		marginBottom: space.xs,
	},
	title: {
		...type.rowTitleSemibold,
		color: colors.text,
		flex: 1,
	},
	meta: {
		...type.secondary,
		color: colors.textSecondary,
	},
	actions: {
		marginTop: space.md,
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
	},
	actionButton: {
		flexGrow: 1,
		flexBasis: 140,
	},
});

export default PendingStockListItem;
